import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../lib/api';
import { isSuperAdmin, getCurrentAccount } from '../../lib/auth';
import { MainLayout } from '../../components/MainLayout';
import { Skeleton } from '../../components/ui/skeleton';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../components/ui/tooltip';
import { toast } from 'sonner';
import SuperAdminByCustomerCard from './SuperAdminByCustomerCard';
import {
  AlertTriangle, Clock, FileText,
  ArrowRight, Building, CreditCard, ShieldAlert,
  CheckCircle, XCircle, Layers, Zap, HelpCircle, Timer,
} from 'lucide-react';

const fmtC = (v) => v == null ? '\u2014' : '$' + Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const DUE_TABS = [
  { key: '1d', label: '<24h' },
  { key: '3d', label: '<3d' },
  { key: '5d', label: '<5d' },
  { key: '7d', label: '<7d' },
];
const OVERDUE_TABS = [
  { key: 'all', label: 'All' },
  { key: '3d', label: '>3d' },
  { key: '5d', label: '>5d' },
  { key: '7d', label: '>7d' },
];

const DUE_DAYS_MAP = { '1d': 1, '3d': 3, '5d': 5, '7d': 7 };
const OVERDUE_DAYS_MAP = { 'all': 0, '3d': 3, '5d': 5, '7d': 7 };

export default function PaymentsDashboard({ isGlobal = false, noLayout = false }) {
  const navigate = useNavigate();
  const currentAccount = getCurrentAccount();
  const isSuper = isSuperAdmin();
  const effectiveGlobal = isGlobal && isSuper;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dueTab, setDueTab] = useState('3d');
  const [overdueTab, setOverdueTab] = useState('all');
  const [hideInactive, setHideInactive] = useState(false);
  const [lateFeeStats, setLateFeeStats] = useState({});

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const params = {};
        if (!effectiveGlobal && currentAccount?.id) params.account_id = currentAccount.id;
        if (hideInactive) params.exclude_inactive = true;
        const { data: d } = await api.get('/payments/v2/workspace/dashboard', { params });
        setData(d);
      } catch (err) {
        toast.error('Failed to load dashboard');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [effectiveGlobal, currentAccount?.id, hideInactive]);

  // Late-fee stats are org-scoped (no global view yet) — quick + cached server-side.
  useEffect(() => {
    if (effectiveGlobal || !currentAccount?.id) {
      setLateFeeStats({});
      return;
    }
    api
      .get('/payments/v2/reports/late-fee/stats', { params: { account_id: currentAccount.id } })
      .then((r) => setLateFeeStats(r.data || {}))
      .catch(() => setLateFeeStats({}));
  }, [effectiveGlobal, currentAccount?.id]);

  const goToWorkspace = (filters = {}) => {
    const base = effectiveGlobal ? '/payments/workspace' : '/payments/bills';
    const params = new URLSearchParams(filters);
    navigate(`${base}?${params.toString()}`);
  };

  if (loading) {
    const skeleton = (
      <div className="space-y-6" data-testid="dashboard-loading">
        <Skeleton className="h-8 w-64" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-36" />)}
        </div>
      </div>
    );
    return noLayout ? skeleton : <MainLayout>{skeleton}</MainLayout>;
  }

  const d = data || {};
  const dueSoon = d.due_soon || {};
  const overdue = d.overdue || {};
  const payments = d.payments || {};
  const pfCov = d.pf_coverage || {};
  const payDist = d.payment_status_dist || {};
  const pfDist = d.pf_status_dist || {};
  const overdueOrg = d.overdue_by_org || [];
  const due3dOrg = d.due_3d_by_org || [];
  const aging = d.in_review_aging || {};
  const refunds = d.refunds || { total_refunded: 0, total_to_refund: 0, refunded_count: 0, to_refund_count: 0 };
  const invSummary = d.invoice_summary || {};
  const missingDue = d.missing_due_date || { total_count: 0, total_amount: 0, by_vendor: [] };

  const curDue = dueSoon[dueTab] || {};
  const curOverdue = overdue[overdueTab] || {};

  // Drill into Bills workspace filtered by in-review/approved pipeline + urgency
  const goToAging = (bucket) => {
    const f = { pf_status: 'review,approved', payment_status: 'unpaid', sort_by: 'due_date', sort_dir: 'asc' };
    if (bucket === 'overdue') f.due_filter = 'overdue';
    else if (bucket === 'due_3d') f.due_within_days = 3;
    else if (bucket === 'due_7d') f.due_within_days = 7;
    else if (bucket === 'due_14d') f.due_within_days = 14;
    goToWorkspace(f);
  };

  const AGING_STYLES = {
    red: 'hover:border-red-200 hover:bg-red-50/40',
    amber: 'hover:border-amber-200 hover:bg-amber-50/40',
    yellow: 'hover:border-yellow-200 hover:bg-yellow-50/40',
    slate: 'hover:border-slate-200 hover:bg-slate-50/40',
  };
  const AGING_ICON = {
    red: 'text-red-600',
    amber: 'text-amber-600',
    yellow: 'text-yellow-600',
    slate: 'text-slate-600',
  };
  const AGING_COUNT = {
    red: 'text-red-700',
    amber: 'text-amber-700',
    yellow: 'text-yellow-700',
    slate: 'text-slate-700',
  };

  const AgingRow = ({ bucket, label, icon: Icon, color, count, amount }) => (
    <button
      onClick={() => goToAging(bucket)}
      className={`group w-full flex items-center justify-between p-2 rounded-md border border-transparent ${AGING_STYLES[color]} transition-all text-left`}
      data-testid={`aging-row-${bucket}`}
    >
      <div className="flex items-center gap-2 min-w-0">
        <Icon className={`h-3.5 w-3.5 ${AGING_ICON[color]} flex-shrink-0`} />
        <span className="text-xs font-medium truncate">{label}</span>
      </div>
      <div className="flex items-center gap-3 flex-shrink-0">
        <span className={`text-sm font-bold ${AGING_COUNT[color]} tabular-nums`}>{(count || 0).toLocaleString()}</span>
        <span className="text-[10px] text-muted-foreground w-20 text-right tabular-nums">{fmtC(amount)}</span>
        <ArrowRight className={`h-3 w-3 ${AGING_ICON[color]} opacity-0 group-hover:opacity-100 transition-opacity`} />
      </div>
    </button>
  );

  const content = (
    <TooltipProvider delayDuration={200}>
    <div className="space-y-5" data-testid="payments-dashboard">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold tracking-tight" data-testid="dashboard-title">
              {effectiveGlobal ? 'Payments Dashboard' : 'Payments Overview'}
            </h2>
            <p className="text-sm text-muted-foreground">Morning overview of payment activity and at-risk bills</p>
          </div>
          {effectiveGlobal && (
            <label className="flex items-center gap-2 cursor-pointer text-xs text-muted-foreground hover:text-foreground transition-colors" data-testid="hide-inactive-toggle">
              <input
                type="checkbox"
                checked={hideInactive}
                onChange={(e) => setHideInactive(e.target.checked)}
                className="rounded border-border h-3.5 w-3.5 accent-foreground"
              />
              Hide inactive customers
            </label>
          )}
        </div>

        {/* Refund Stats — Total Refunded + Amount To Refund + Late Fees */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="refund-stats-row">
          <button
            onClick={() => navigate('/payments/refunds-report?status=to_refund')}
            className="group w-full text-left p-4 border rounded-lg hover:border-amber-300 hover:shadow-sm transition-all bg-card"
            data-testid="card-amount-to-refund"
          >
            <div className="flex items-center gap-2 mb-2">
              <div className="p-1.5 rounded bg-amber-100"><Timer className="h-4 w-4 text-amber-600" /></div>
              <span className="text-xs font-medium text-amber-600 uppercase tracking-wide">Amount To Refund</span>
              <ArrowRight className="h-3 w-3 ml-auto text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
            </div>
            <p className="text-2xl font-bold text-amber-700" data-testid="amount-to-refund-value">{fmtC(refunds.total_to_refund)}</p>
            <p className="text-xs text-muted-foreground mt-1">
              {(refunds.to_refund_count || 0).toLocaleString()} bill{(refunds.to_refund_count || 0) === 1 ? '' : 's'} pending refund processing
            </p>
          </button>
          <div
            className="w-full p-4 border rounded-lg bg-card"
            data-testid="card-total-refunded"
          >
            <div className="flex items-center gap-2 mb-2">
              <div className="p-1.5 rounded bg-emerald-100"><CheckCircle className="h-4 w-4 text-emerald-600" /></div>
              <span className="text-xs font-medium text-emerald-600 uppercase tracking-wide">Total Refunded</span>
            </div>
            <p className="text-2xl font-bold text-emerald-700" data-testid="total-refunded-value">{fmtC(refunds.total_refunded)}</p>
            <p className="text-xs text-muted-foreground mt-1" data-testid="total-refunded-source-split">
              {/* Show per-bill vs bulk breakdown so it's obvious where the money came from */}
              Bill = <strong className="text-foreground" data-testid="refund-source-bill-count">{(refunds.refunded_bill_count ?? (refunds.refunded_bill > 0 ? refunds.refunded_count : 0))}</strong>
              {' '}|{' '}Bulk Refund = <strong className="text-foreground" data-testid="refund-source-bulk-count">{(refunds.refunded_bulk_count ?? (refunds.refunded_bulk > 0 ? Math.max(0, (refunds.refunded_count || 0) - 0) : 0))}</strong>
            </p>
          </div>
          <button
            onClick={() => navigate('/payments/late-fee-report')}
            className={`group w-full text-left p-4 border rounded-lg hover:border-rose-300 hover:shadow-sm transition-all bg-card ${effectiveGlobal ? 'hidden' : ''}`}
            data-testid="card-late-fees"
          >
            <div className="flex items-center gap-2 mb-2">
              <div className="p-1.5 rounded bg-rose-100"><Timer className="h-4 w-4 text-rose-600" /></div>
              <span className="text-xs font-medium text-rose-600 uppercase tracking-wide">Late Fees Incurred</span>
              <ArrowRight className="h-3 w-3 ml-auto text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
            </div>
            <p className="text-2xl font-bold text-rose-700" data-testid="late-fees-value">{fmtC(lateFeeStats.total_late_fee)}</p>
            <p className="text-xs text-muted-foreground mt-1">
              {(lateFeeStats.bill_count || 0).toLocaleString()} bill{(lateFeeStats.bill_count || 0) === 1 ? '' : 's'}
              {lateFeeStats.due_before_approval_cnt > 0 && (
                <> · <span className="text-amber-700 font-medium">{lateFeeStats.due_before_approval_cnt} due before approval</span></>
              )}
            </p>
          </button>
        </div>

        {/* Invoice Summary */}
        <div className="grid grid-cols-6 gap-3" data-testid="invoice-summary-row">
          <div className="p-4 border rounded-lg bg-card" data-testid="inv-total">
            <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1">Total Invoices</p>
            <p className="text-xl font-bold">{(invSummary.total?.count || 0).toLocaleString()}</p>
            <p className="text-[10px] text-muted-foreground">{fmtC(invSummary.total?.value)}</p>
          </div>
          <div className="p-4 border rounded-lg bg-card" data-testid="inv-review">
            <p className="text-[10px] font-semibold text-amber-600 uppercase tracking-wider mb-1">In Review</p>
            <p className="text-xl font-bold text-amber-700">{(invSummary.in_review?.count || 0).toLocaleString()}</p>
            <p className="text-[10px] text-muted-foreground">{fmtC(invSummary.in_review?.requested)}</p>
          </div>
          <div className="p-4 border rounded-lg bg-card" data-testid="inv-approved">
            <p className="text-[10px] font-semibold text-blue-600 uppercase tracking-wider mb-1">In Approved</p>
            <p className="text-xl font-bold text-blue-700">{(invSummary.in_approved?.count || 0).toLocaleString()}</p>
            <p className="text-[10px] text-muted-foreground">{fmtC(invSummary.in_approved?.requested)}</p>
          </div>
          <div className="p-4 border rounded-lg bg-card" data-testid="inv-funds-pulled">
            <p className="text-[10px] font-semibold text-violet-600 uppercase tracking-wider mb-1">Total Funds Pulled</p>
            <p className="text-xl font-bold text-violet-700">{fmtC(invSummary.funds_pulled?.total)}</p>
            <p className="text-[10px] text-muted-foreground">Bank: {fmtC(invSummary.funds_pulled?.bank)}</p>
          </div>
          <div className="p-4 border rounded-lg bg-card" data-testid="inv-paid">
            <p className="text-[10px] font-semibold text-emerald-600 uppercase tracking-wider mb-1">Invoices Paid</p>
            <p className="text-xl font-bold text-emerald-700">{(invSummary.paid?.count || 0).toLocaleString()}</p>
            <p className="text-[10px] text-muted-foreground" title="Total billed amount across paid invoices (requested). Use the Total Paid card for actual cash flow.">{fmtC(invSummary.paid?.requested ?? invSummary.paid?.value)}</p>
          </div>
          <div className="p-4 border rounded-lg bg-card" data-testid="inv-unpaid">
            <p className="text-[10px] font-semibold text-red-600 uppercase tracking-wider mb-1">Invoices Unpaid</p>
            <p className="text-xl font-bold text-red-700">{(invSummary.unpaid?.count || 0).toLocaleString()}</p>
            <p className="text-[10px] text-muted-foreground">{fmtC(invSummary.unpaid?.value)}</p>
          </div>
        </div>

        {/* Top 3-Column Summary */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* COL 1 — At-Risk (Overdue + Due Soon stacked) */}
          <div className="space-y-3">
            <button
              onClick={() => goToWorkspace({ payment_status: 'unpaid', due_filter: 'overdue', sort_by: 'due_date', sort_dir: 'asc' })}
              className="group w-full text-left p-4 border rounded-lg hover:border-red-300 hover:shadow-sm transition-all bg-card"
              data-testid="card-overdue"
            >
              <div className="flex items-center gap-2 mb-2">
                <div className="p-1.5 rounded bg-red-100"><ShieldAlert className="h-4 w-4 text-red-600" /></div>
                <span className="text-xs font-medium text-red-600 uppercase tracking-wide">Overdue (All Unpaid)</span>
                <ArrowRight className="h-3 w-3 ml-auto text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
              <p className="text-2xl font-bold text-red-700" data-testid="overdue-count">{(curOverdue.total_count || 0).toLocaleString()}</p>
              <p className="text-xs text-muted-foreground mt-1">{fmtC(curOverdue.total_amount)} unpaid</p>
              {(overdue.in_review || overdue.in_approved) && (
                <div className="mt-3 pt-3 border-t flex items-center gap-3 text-[11px]">
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); goToWorkspace({ payment_status: 'unpaid', due_filter: 'overdue', pf_status: 'review', sort_by: 'due_date', sort_dir: 'asc' }); }}
                    className="flex-1 text-left hover:bg-orange-50 rounded p-1.5 -m-1.5 transition-colors"
                    data-testid="overdue-in-review-btn"
                    title="Open Global Workspace filtered to overdue unpaid bills inside Review PFs"
                  >
                    <span className="block text-[9px] font-semibold uppercase tracking-wider text-orange-600">In Review</span>
                    <span className="block font-bold text-orange-700 text-sm" data-testid="overdue-in-review-count">{(overdue.in_review?.count || 0).toLocaleString()}</span>
                    <span className="block text-muted-foreground text-[10px]">{fmtC(overdue.in_review?.amount)}</span>
                  </button>
                  <div className="w-px h-8 bg-border" />
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); goToWorkspace({ payment_status: 'unpaid', due_filter: 'overdue', pf_status: 'approved', sort_by: 'due_date', sort_dir: 'asc' }); }}
                    className="flex-1 text-left hover:bg-blue-50 rounded p-1.5 -m-1.5 transition-colors"
                    data-testid="overdue-in-approved-btn"
                    title="Open Global Workspace filtered to overdue unpaid bills inside Approved PFs"
                  >
                    <span className="block text-[9px] font-semibold uppercase tracking-wider text-blue-600">In Approved</span>
                    <span className="block font-bold text-blue-700 text-sm" data-testid="overdue-in-approved-count">{(overdue.in_approved?.count || 0).toLocaleString()}</span>
                    <span className="block text-muted-foreground text-[10px]">{fmtC(overdue.in_approved?.amount)}</span>
                  </button>
                </div>
              )}
            </button>

            <button
              onClick={() => goToWorkspace({ payment_status: 'unpaid', due_within_days: DUE_DAYS_MAP[dueTab], sort_by: 'due_date', sort_dir: 'asc' })}
              className="group w-full text-left p-4 border rounded-lg hover:border-amber-300 hover:shadow-sm transition-all bg-card"
              data-testid="card-due-soon"
            >
              <div className="flex items-center gap-2 mb-2">
                <div className="p-1.5 rounded bg-amber-100"><AlertTriangle className="h-4 w-4 text-amber-600" /></div>
                <span className="text-xs font-medium text-amber-600 uppercase tracking-wide">Due &lt; {DUE_TABS.find(t => t.key === dueTab)?.label}</span>
                <ArrowRight className="h-3 w-3 ml-auto text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
              <p className="text-2xl font-bold text-amber-700" data-testid="due-soon-count">{(curDue.total_count || 0).toLocaleString()}</p>
              <p className="text-xs text-muted-foreground mt-1">{fmtC(curDue.total_amount)} at risk</p>
            </button>

            {/* "No Due Date" — bills that fall through every time-bucketed
                tile above. Only render when there's something to show. */}
            {missingDue.total_count > 0 && (
              <button
                onClick={() => goToWorkspace({ payment_status: 'unpaid', due_filter: 'missing', sort_by: 'supplier_name', sort_dir: 'asc' })}
                className="group w-full text-left p-3 border rounded-lg hover:border-slate-300 hover:shadow-sm transition-all bg-card border-dashed"
                data-testid="card-no-due-date"
                title="Bills with no due_date — invisible to every other tile"
              >
                <div className="flex items-center gap-2">
                  <div className="p-1 rounded bg-slate-100"><HelpCircle className="h-3.5 w-3.5 text-slate-600" /></div>
                  <span className="text-[11px] font-semibold text-slate-600 uppercase tracking-wide flex-1">No Due Date</span>
                  <span className="text-sm font-bold text-slate-700" data-testid="no-due-date-count">{missingDue.total_count.toLocaleString()}</span>
                  <ArrowRight className="h-3 w-3 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
                <p className="text-[10px] text-muted-foreground mt-1 ml-7">
                  {fmtC(missingDue.total_amount)} unscheduled
                  {missingDue.by_vendor?.[0] && (
                    <span className="ml-1">— mostly {missingDue.by_vendor[0].name}</span>
                  )}
                </p>
              </button>
            )}
          </div>

          {/* COL 2 — In-Review Aging */}
          <div className="p-4 border rounded-lg bg-card border-orange-200/60" data-testid="card-in-review-aging">
            <div className="flex items-center gap-2 mb-3">
              <div className="p-1.5 rounded bg-orange-100"><Timer className="h-4 w-4 text-orange-600" /></div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold text-orange-700 uppercase tracking-wide">Bills Aging</p>
                <p className="text-[10px] text-muted-foreground truncate">
                  Bills in {(aging.pending_pf_count || 0)} pending PFs (Review + Approved)
                </p>
              </div>
              <Tooltip>
                <TooltipTrigger asChild>
                  <HelpCircle className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
                </TooltipTrigger>
                <TooltipContent className="max-w-xs">
                  <p className="text-xs">
                    Bills sitting in Payment Files that customers haven&apos;t paid yet (status: <strong>Review</strong> or <strong>Approved</strong>).
                    These are most at risk of going overdue while waiting for customer action.
                  </p>
                </TooltipContent>
              </Tooltip>
            </div>
            <div className="space-y-1">
              <AgingRow bucket="overdue" label="Already Overdue" icon={ShieldAlert} color="red" count={aging.overdue?.total_count} amount={aging.overdue?.total_amount} />
              <AgingRow bucket="due_3d" label="Due in < 3 days" icon={AlertTriangle} color="amber" count={aging.due_3d?.total_count} amount={aging.due_3d?.total_amount} />
              <AgingRow bucket="due_7d" label="Due in < 7 days" icon={Clock} color="yellow" count={aging.due_7d?.total_count} amount={aging.due_7d?.total_amount} />
              <AgingRow bucket="due_14d" label="Due in < 14 days" icon={Clock} color="slate" count={aging.due_14d?.total_count} amount={aging.due_14d?.total_amount} />
            </div>
          </div>

          {/* COL 3 — Coverage + Payments (stacked) */}
          <div className="space-y-3">
            {/* PF Coverage */}
            <div className="p-4 border rounded-lg bg-card" data-testid="card-pf-coverage">
              <div className="flex items-center gap-2 mb-2">
                <div className="p-1.5 rounded bg-slate-100"><Layers className="h-4 w-4 text-slate-600" /></div>
                <span className="text-xs font-medium text-slate-600 uppercase tracking-wide">PF Coverage</span>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <HelpCircle className="h-3.5 w-3.5 ml-auto text-muted-foreground cursor-help" />
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs">
                    <p className="text-xs space-y-1">
                      <span className="block"><strong>In PF:</strong> bills already batched into a Payment File.</span>
                      <span className="block"><strong>Not yet batched:</strong> parsed bills that haven&apos;t been included in any Payment File run yet (awaiting next cycle or not approved at source).</span>
                    </p>
                  </TooltipContent>
                </Tooltip>
              </div>
              <div className="flex items-baseline gap-2">
                <p className="text-2xl font-bold">{(pfCov.in_pf || 0).toLocaleString()}</p>
                <span className="text-xs text-muted-foreground">in PF</span>
              </div>
              <p className="text-xs text-muted-foreground mt-1">{(pfCov.not_in_pf || 0).toLocaleString()} parsed, not yet batched</p>
            </div>

            {/* Payment Status Summary */}
            <div className="p-4 border rounded-lg bg-card hover:border-emerald-300 hover:shadow-sm transition-all" data-testid="card-payment-summary">
              <div className="flex items-center gap-2 mb-2">
                <div className="p-1.5 rounded bg-emerald-100"><CreditCard className="h-4 w-4 text-emerald-600" /></div>
                <span className="text-xs font-medium text-emerald-600 uppercase tracking-wide">Payments</span>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <HelpCircle className="h-3.5 w-3.5 ml-auto text-muted-foreground cursor-help" />
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs">
                    <p className="text-xs">
                      Paid / Unpaid bill totals across all PFs. Sub-stats show <strong>unpaid</strong> bills split by the PF status they sit in (Review or Approved).
                    </p>
                  </TooltipContent>
                </Tooltip>
              </div>
              <div className="flex items-baseline gap-3 mb-1">
                <p className="text-2xl font-bold text-emerald-700" data-testid="payments-paid-count">{(payDist.paid?.count || 0).toLocaleString()}</p>
                <span className="text-xs text-muted-foreground">paid</span>
                <span className="ml-auto text-xs text-amber-600 font-medium" data-testid="payments-unpaid-count">{(payDist.unpaid?.count || 0).toLocaleString()} unpaid</span>
              </div>
              {(payDist.hold?.count || 0) > 0 && (
                <p className="text-[10px] text-red-600 mb-1">{payDist.hold.count} on hold</p>
              )}
              {(payments.in_review || payments.in_approved) && (
                <div className="mt-3 pt-3 border-t flex items-center gap-3 text-[11px]">
                  <button
                    type="button"
                    onClick={() => goToWorkspace({ payment_status: 'unpaid', pf_status: 'review', sort_by: 'due_date', sort_dir: 'asc' })}
                    className="flex-1 text-left hover:bg-orange-50 rounded p-1.5 -m-1.5 transition-colors"
                    data-testid="payments-in-review-btn"
                    title="Open Global Workspace filtered to unpaid bills in Review PFs"
                  >
                    <span className="block text-[9px] font-semibold uppercase tracking-wider text-orange-600">In Review</span>
                    <span className="block font-bold text-orange-700 text-sm" data-testid="payments-in-review-count">{(payments.in_review?.count || 0).toLocaleString()}</span>
                    <span className="block text-muted-foreground text-[10px]">{fmtC(payments.in_review?.amount)}</span>
                  </button>
                  <div className="w-px h-8 bg-border" />
                  <button
                    type="button"
                    onClick={() => goToWorkspace({ payment_status: 'unpaid', pf_status: 'approved', sort_by: 'due_date', sort_dir: 'asc' })}
                    className="flex-1 text-left hover:bg-blue-50 rounded p-1.5 -m-1.5 transition-colors"
                    data-testid="payments-in-approved-btn"
                    title="Open Global Workspace filtered to unpaid bills in Approved PFs"
                  >
                    <span className="block text-[9px] font-semibold uppercase tracking-wider text-blue-600">In Approved</span>
                    <span className="block font-bold text-blue-700 text-sm" data-testid="payments-in-approved-count">{(payments.in_approved?.count || 0).toLocaleString()}</span>
                    <span className="block text-muted-foreground text-[10px]">{fmtC(payments.in_approved?.amount)}</span>
                  </button>
                </div>
              )}
              <div className="pt-2 mt-2 border-t flex gap-3 text-[10px] text-muted-foreground">
                <span>{pfDist.review || 0} Review PFs</span>
                <span>{pfDist.approved || 0} Approved PFs</span>
                <span>{pfDist.paid || 0} Paid PFs</span>
              </div>
            </div>
          </div>
        </div>

        {/* Vendor Breakdown Sections */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Due Soon by Vendor */}
          <div className="border rounded-lg p-4 bg-card" data-testid="due-vendor-card">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-amber-500" /> Due Soon by Vendor
              </h3>
              <div className="flex gap-0.5 bg-muted rounded p-0.5" data-testid="due-tabs">
                {DUE_TABS.map(t => (
                  <button
                    key={t.key}
                    onClick={() => setDueTab(t.key)}
                    className={`px-2 py-0.5 text-[10px] font-medium rounded transition-colors ${dueTab === t.key ? 'bg-amber-500 text-white' : 'text-muted-foreground hover:text-foreground'}`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex items-center justify-between mb-2 text-xs text-muted-foreground border-b pb-1.5">
              <span>{(curDue.total_count || 0).toLocaleString()} bills</span>
              <span>{fmtC(curDue.total_amount)}</span>
            </div>
            <div className="space-y-1 max-h-[240px] overflow-y-auto">
              {(curDue.by_vendor || []).length === 0 ? (
                <p className="text-xs text-muted-foreground py-4 text-center">No bills due in this range</p>
              ) : (
                (curDue.by_vendor || []).map((v, i) => (
                  <button
                    key={v.name}
                    onClick={() => goToWorkspace({ vendor: v.name, payment_status: 'unpaid', due_within_days: DUE_DAYS_MAP[dueTab], sort_by: 'due_date', sort_dir: 'asc' })}
                    className="w-full flex items-center justify-between p-1.5 rounded hover:bg-muted/50 transition-colors text-left group"
                    data-testid={`due-vendor-${i}`}
                  >
                    <span className="text-xs truncate max-w-[180px]">{v.name}</span>
                    <div className="flex items-center gap-3 flex-shrink-0">
                      <span className="text-xs text-amber-600 font-semibold">{v.count}</span>
                      <span className="text-[10px] text-muted-foreground w-20 text-right">{fmtC(v.total_amount)}</span>
                      <ArrowRight className="h-3 w-3 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>

          {/* Overdue by Vendor */}
          <div className="border rounded-lg p-4 bg-card" data-testid="overdue-vendor-card">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold flex items-center gap-2">
                <ShieldAlert className="h-4 w-4 text-red-500" /> Overdue by Vendor
              </h3>
              <div className="flex gap-0.5 bg-muted rounded p-0.5" data-testid="overdue-tabs">
                {OVERDUE_TABS.map(t => (
                  <button
                    key={t.key}
                    onClick={() => setOverdueTab(t.key)}
                    className={`px-2 py-0.5 text-[10px] font-medium rounded transition-colors ${overdueTab === t.key ? 'bg-red-500 text-white' : 'text-muted-foreground hover:text-foreground'}`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex items-center justify-between mb-2 text-xs text-muted-foreground border-b pb-1.5">
              <span>{(curOverdue.total_count || 0).toLocaleString()} bills</span>
              <span>{fmtC(curOverdue.total_amount)}</span>
            </div>
            <div className="space-y-1 max-h-[240px] overflow-y-auto">
              {(curOverdue.by_vendor || []).length === 0 ? (
                <p className="text-xs text-muted-foreground py-4 text-center">No overdue bills in this range</p>
              ) : (
                (curOverdue.by_vendor || []).map((v, i) => (
                  <button
                    key={v.name}
                    onClick={() => goToWorkspace({ vendor: v.name, payment_status: 'unpaid', due_filter: 'overdue', sort_by: 'due_date', sort_dir: 'asc' })}
                    className="w-full flex items-center justify-between p-1.5 rounded hover:bg-muted/50 transition-colors text-left group"
                    data-testid={`overdue-vendor-${i}`}
                  >
                    <span className="text-xs truncate max-w-[180px]">{v.name}</span>
                    <div className="flex items-center gap-3 flex-shrink-0">
                      <span className="text-xs text-red-600 font-semibold">{v.count}</span>
                      <span className="text-[10px] text-muted-foreground w-20 text-right">{fmtC(v.total_amount)}</span>
                      <ArrowRight className="h-3 w-3 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>

          {/* At-Risk by Org (global only) */}
          {effectiveGlobal && overdueOrg.length > 0 && (
            <div className="border rounded-lg p-4 bg-card" data-testid="org-breakdown">
              <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
                <Building className="h-4 w-4" /> Overdue by Customer
              </h3>
              <div className="space-y-1 max-h-[240px] overflow-y-auto">
                {overdueOrg.map((org) => (
                  <button
                    key={org.account_id}
                    onClick={() => goToWorkspace({ org: org.account_id, payment_status: 'unpaid', due_filter: 'overdue', sort_by: 'due_date', sort_dir: 'asc' })}
                    className="w-full flex items-center justify-between p-1.5 rounded hover:bg-muted/50 transition-colors text-left group"
                    data-testid={`org-row-${org.account_id}`}
                  >
                    <span className="text-xs font-medium truncate max-w-[180px]">{org.org_name || 'Unknown'}</span>
                    <div className="flex items-center gap-3 flex-shrink-0">
                      <span className="text-xs text-red-600 font-semibold">{org.count} overdue</span>
                      <span className="text-[10px] text-muted-foreground">{fmtC(org.total_amount)}</span>
                      <ArrowRight className="h-3 w-3 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Due Soon by Org (global only) */}
          {effectiveGlobal && due3dOrg.length > 0 && (
            <div className="border rounded-lg p-4 bg-card" data-testid="due-org-breakdown">
              <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-amber-500" /> Due &lt;3d by Customer
              </h3>
              <div className="space-y-1 max-h-[240px] overflow-y-auto">
                {due3dOrg.map((org) => (
                  <button
                    key={org.account_id}
                    onClick={() => goToWorkspace({ org: org.account_id, payment_status: 'unpaid', due_within_days: 3, sort_by: 'due_date', sort_dir: 'asc' })}
                    className="w-full flex items-center justify-between p-1.5 rounded hover:bg-muted/50 transition-colors text-left group"
                    data-testid={`due-org-row-${org.account_id}`}
                  >
                    <span className="text-xs font-medium truncate max-w-[180px]">{org.org_name || 'Unknown'}</span>
                    <div className="flex items-center gap-3 flex-shrink-0">
                      <span className="text-xs text-amber-600 font-semibold">{org.count} bills</span>
                      <span className="text-[10px] text-muted-foreground">{fmtC(org.total_amount)}</span>
                      <ArrowRight className="h-3 w-3 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Super Admin: By-Customer materialized snapshot — moved out of dashboard */}
        {/* {effectiveGlobal && <SuperAdminByCustomerCard />} */}
      </div>
    </TooltipProvider>
  );

  return noLayout ? content : <MainLayout>{content}</MainLayout>;
}
